import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { Bitcoin, Plus, Edit2, Trash2, Save, X, TrendingUp, Clock, History } from 'lucide-react';

const defaultForm = { name: '', description: '', price: '', lockingMonths: '', monthlyReturnPercent: '', returnFrequency: 'monthly', isEnabled: true };

export default function InvestorPackages() {
  const [packages, setPackages] = useState([]);
  const [allInvestments, setAllInvestments] = useState([]);
  const [tab, setTab] = useState('packages');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [triggerModal, setTriggerModal] = useState(null); // package for trigger
  const [triggerPercent, setTriggerPercent] = useState('');
  const [triggerNote, setTriggerNote] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [selectedUserStats, setSelectedUserStats] = useState(null);

  const fetchAll = async () => {
    const [pkgRes, allRes] = await Promise.all([
      axios.get('/api/investment/admin/packages'),
      axios.get('/api/investment/admin/all')
    ]);
    setPackages(pkgRes.data.packages || []);
    setAllInvestments(allRes.data.investments || []);
  };
  useEffect(() => { fetchAll(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`/api/investment/admin/packages/${editing}`, form);
        toast.success('Package updated successfully!');
      } else {
        await axios.post('/api/investment/admin/packages', form);
        toast.success('Package created successfully!');
      }
      setShowForm(false); setEditing(null); setForm(defaultForm); fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed');
    } finally { setLoading(false); }
  };

  // Change 1: Trigger - set this month's return
  const handleTrigger = async () => {
    if (!triggerPercent || parseFloat(triggerPercent) <= 0) return toast.error('Valid % daalo');
    setTriggerLoading(true);
    try {
      const res = await axios.post(`/api/investment/admin/packages/${triggerModal._id}/set-return`, {
        returnPercent: parseFloat(triggerPercent),
        note: triggerNote
      });
      toast.success(res.data.message);
      setTriggerModal(null); setTriggerPercent(''); setTriggerNote('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed');
    } finally { setTriggerLoading(false); }
  };

  const handleToggleInvestmentStatus = async (investment) => {
    try {
      const confirmText = investment.status === 'active'
        ? 'Are you sure you want to deactivate this investment plan?'
        : 'Are you sure you want to activate this investment plan?';
      if (!window.confirm(confirmText)) return;

      const res = await axios.put(`/api/investment/admin/investments/${investment._id}/status`);
      toast.success(res.data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  const inputClass = "w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500";
  const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  const getInvestmentProfits = (inv) => {
    const returnPercent = inv.package?.currentReturnPercent ?? inv.monthlyReturnPercent ?? 0;
    const frequency = inv.returnFrequency || inv.package?.returnFrequency || 'monthly';
    const amount = inv.amountInvested || 0;
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() || 30;

    let dailyProfit = 0;
    let weeklyProfit = 0;
    let monthlyProfit = 0;
    let yearlyProfit = 0;

    if (frequency === 'daily') {
      dailyProfit = (amount * returnPercent) / 100;
      weeklyProfit = dailyProfit * 7;
      monthlyProfit = dailyProfit * daysInMonth;
      yearlyProfit = dailyProfit * 365;
    } else if (frequency === 'weekly') {
      weeklyProfit = (amount * returnPercent) / 100;
      dailyProfit = weeklyProfit / 7;
      monthlyProfit = weeklyProfit * 4;
      yearlyProfit = weeklyProfit * 52;
    } else {
      monthlyProfit = (amount * returnPercent) / 100;
      weeklyProfit = monthlyProfit / 4;
      dailyProfit = monthlyProfit / daysInMonth;
      yearlyProfit = monthlyProfit * 12;
    }

    return { returnPercent, frequency, dailyProfit, weeklyProfit, monthlyProfit, yearlyProfit };
  };

  const computeUserStats = (userId) => {
    let daily = 0, weekly = 0, monthly = 0, yearly = 0, totalInvested = 0;
    let actualEarnedTotal = 0;
    let actualEarnedThisMonth = 0;
    const investments = [];
    allInvestments.forEach(inv => {
      const uid = inv.user?._id || inv.userId;
      if (!uid) return;
      if (uid.toString() === (userId || '').toString()) {
        const { dailyProfit, weeklyProfit, monthlyProfit, yearlyProfit } = getInvestmentProfits(inv);
        daily += dailyProfit;
        weekly += weeklyProfit;
        monthly += monthlyProfit;
        yearly += yearlyProfit;
        totalInvested += (inv.amountInvested || 0);
        const earnedTotal = inv.totalCommissionPaid ?? inv.totalEarned ?? inv.earned ?? inv.earnedTotal ?? 0;
        const earnedThisMonth = inv.monthEarnings ?? inv.earnedThisMonth ?? inv.monthlyPaid ?? inv.paidThisMonth ?? 0;
        actualEarnedTotal += (earnedTotal || 0);
        actualEarnedThisMonth += (earnedThisMonth || 0);
        investments.push({ ...inv, monthlyProfit, weeklyProfit, dailyProfit, actualEarned: earnedTotal, actualEarnedThisMonth: earnedThisMonth });
      }
    });
    return { daily, weekly, monthly, yearly, totalInvested, actualEarnedTotal, actualEarnedThisMonth, investments };
  };

  const [processLoading, setProcessLoading] = useState(false);
  const processUserReturns = async (userId) => {
    try {
      setProcessLoading(true);
      const res = await axios.post(`/api/investment/admin/process-user-returns/${userId}`);
      await fetchAll();
      return res.data;
    } catch (e) {
      console.error('Process user returns failed', e.message);
      return { processed: 0, message: 'Process failed' };
    } finally {
      setProcessLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Bitcoin className="w-6 h-6 text-amber-400" /> Investor Packages
            </h1>
            <p className="text-gray-400 mt-1">Manage packages and set monthly returns</p>
          </div>
          {!showForm && (
            <button onClick={() => { setShowForm(true); setEditing(null); setForm(defaultForm); }}
              className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Package
            </button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-dark-700 border border-primary-500/30 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">{editing ? 'Edit Package' : 'New Package'}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); setForm(defaultForm); }}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div><label className="block text-xs text-gray-400 mb-1">Package Name</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputClass} required /></div>
                <div><label className="block text-xs text-gray-400 mb-1">Price ($)</label>
                  <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className={inputClass} required min="1" /></div>
                <div><label className="block text-xs text-gray-400 mb-1">Lock Duration (months)</label>
                  <input type="number" value={form.lockingMonths} onChange={e => setForm(f => ({ ...f, lockingMonths: e.target.value }))} className={inputClass} required min="1" /></div>
                <div><label className="block text-xs text-gray-400 mb-1">Max Return % (shown to user as "Up to X%")</label>
                  <input type="number" value={form.monthlyReturnPercent} onChange={e => setForm(f => ({ ...f, monthlyReturnPercent: e.target.value }))} className={inputClass} required min="0.1" step="0.1" /></div>
                <div><label className="block text-xs text-gray-400 mb-1">Return Frequency</label>
                  <select value={form.returnFrequency} onChange={e => setForm(f => ({ ...f, returnFrequency: e.target.value }))} className={inputClass}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div><label className="block text-xs text-gray-400 mb-1">Description (optional)</label>
                  <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inputClass} /></div>
                <div className="flex items-center gap-3 pt-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div className="relative" onClick={() => setForm(f => ({ ...f, isEnabled: !f.isEnabled }))}>
                      <div className={`w-10 h-5 rounded-full transition-colors ${form.isEnabled ? 'bg-primary-500' : 'bg-dark-500'}`}>
                        <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${form.isEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                      </div>
                    </div>
                    <span className="text-gray-300 text-sm">Enabled</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={loading} className="bg-primary-600 hover:bg-primary-500 text-white font-semibold px-6 py-2.5 rounded-xl flex items-center gap-2">
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><Save className="w-4 h-4" /> {editing ? 'Update' : 'Create'}</>}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(defaultForm); }} className="px-4 py-2.5 bg-dark-600 border border-white/10 text-gray-400 hover:text-white rounded-xl">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['packages', 'investments'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5'}`}>
              {t === 'investments' ? `All Investments (${allInvestments.length})` : 'Packages'}
            </button>
          ))}
        </div>

        {/* Packages */}
        {tab === 'packages' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {packages.map(pkg => (
              <div key={pkg._id} className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-600/5 p-5 border-b border-white/5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-bold text-lg">{pkg.name}</p>
                      {pkg.description && <p className="text-gray-400 text-xs mt-0.5">{pkg.description}</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${pkg.isEnabled ? 'bg-primary-500/20 text-primary-300' : 'bg-gray-500/20 text-gray-400'}`}>
                      {pkg.isEnabled ? 'Active' : 'Off'}
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-400">Price</span><span className="text-white font-semibold">${pkg.price.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Lock Duration</span><span className="text-white">{pkg.lockingMonths} months</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Frequency</span><span className="text-white capitalize">{pkg.returnFrequency || 'monthly'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Max Return</span><span className="text-gray-300">Up to {pkg.monthlyReturnPercent}%</span></div>

                  {/* Change 1: Current month return set by admin */}
                  <div className="bg-dark-600 rounded-xl p-3 mt-2">
                    <p className="text-gray-500 text-xs mb-1">{currentMonth} — Actual Return</p>
                    {pkg.currentReturnPercent > 0 ? (
                      <p className="text-primary-400 font-bold text-lg">{pkg.currentReturnPercent}%
                        <span className="text-gray-500 text-xs font-normal ml-2">{pkg.currentReturnNote}</span>
                      </p>
                    ) : (
                      <p className="text-amber-400 text-sm">Not configured yet</p>
                    )}
                  </div>

                  {/* Return History */}
                  {pkg.returnHistory?.length > 0 && (
                    <div className="bg-dark-600 rounded-xl p-2">
                      <p className="text-gray-500 text-xs mb-1 flex items-center gap-1"><History className="w-3 h-3" /> History</p>
                      {pkg.returnHistory.slice(-3).reverse().map((h, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-gray-500">{h.period || h.month || '—'} • {h.frequency || pkg.returnFrequency || 'monthly'}</span>
                          <span className="text-primary-400">{h.returnPercent}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 pt-0 space-y-2">
                  {/* Change 1: Trigger Button */}
                  <button onClick={() => { setTriggerModal(pkg); setTriggerPercent(String(pkg.currentReturnPercent || '')); setTriggerNote(pkg.currentReturnNote || ''); }}
                    className="w-full bg-amber-600/20 border border-amber-500/30 text-amber-300 hover:bg-amber-600/30 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                    <TrendingUp className="w-4 h-4" /> Set {pkg.returnFrequency || 'monthly'} Return
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(pkg._id); setForm({ name: pkg.name, description: pkg.description||'', price: pkg.price, lockingMonths: pkg.lockingMonths, monthlyReturnPercent: pkg.monthlyReturnPercent, returnFrequency: pkg.returnFrequency || 'monthly', isEnabled: pkg.isEnabled }); setShowForm(true); }}
                      className="flex-1 flex items-center justify-center gap-1 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 py-2 rounded-xl text-sm">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={async () => { if(window.confirm('Delete?')) { await axios.delete(`/api/investment/admin/packages/${pkg._id}`); toast.success('Package deleted.'); fetchAll(); }}}
                      className="flex-1 flex items-center justify-center gap-1 bg-red-600/20 text-red-400 hover:bg-red-600/30 py-2 rounded-xl text-sm">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {packages.length === 0 && <div className="col-span-3 text-center py-12 text-gray-500">No packages yet</div>}
          </div>
        )}

        {/* All Investments */}
        {tab === 'investments' && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/5">
                  {['User','Package','Amount','Return %','Monthly','Weekly','Daily','Unlock','Status','Earned','Action'].map(h => (
                    <th key={h} className="px-4 py-3 text-gray-400 font-medium text-left">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {allInvestments.length === 0
                    ? <tr><td colSpan={11} className="text-center py-12 text-gray-500">No investments</td></tr>
                    : allInvestments.map(inv => {
                      const { returnPercent, monthlyProfit, weeklyProfit, dailyProfit } = getInvestmentProfits(inv);
                      return (
                        <tr key={inv._id} className="border-b border-white/5 hover:bg-white/2">
                          <td className="px-4 py-3"><p className="text-white font-medium">{inv.user?.name}</p><p className="text-gray-500 text-xs">{inv.user?.email}</p></td>
                          <td className="px-4 py-3 text-gray-300">{inv.packageName}</td>
                          <td className="px-4 py-3 text-white font-semibold">${inv.amountInvested?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-primary-400">{returnPercent.toFixed(2)}%</td>
                          <td className="px-4 py-3 text-white">${monthlyProfit.toFixed(2)}</td>
                          <td className="px-4 py-3 text-white">${weeklyProfit.toFixed(2)}</td>
                          <td className="px-4 py-3 text-white">${dailyProfit.toFixed(2)}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{new Date(inv.unlockDate).toLocaleDateString()}</td>
                          <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${inv.status==='active'?'bg-primary-500/20 text-primary-300':inv.status==='pending'?'bg-amber-500/20 text-amber-300':'bg-red-500/20 text-red-300'}`}>{inv.status}</span></td>
                          <td className="px-4 py-3 text-primary-400">${(inv.totalCommissionPaid||0).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            {inv.status === 'completed' ? (
                                <div className="space-y-2">
                                  <span className="text-gray-400 text-xs">No action</span>
                                  <button onClick={async () => {
                                    const uid = inv.user?._id || inv.userId;
                                    const stats = computeUserStats(uid);
                                    // fetch actual daily profit records for this user
                                    try {
                                      const startDate = inv.createdAt ? new Date(inv.createdAt).toISOString().split('T')[0] : undefined;
                                      const endDate = new Date().toISOString().split('T')[0];
                                      const res = await axios.get('/api/profit/admin/all', { params: { userId: uid, startDate, endDate, limit: 100 } });
                                      const dailyProfits = res.data.profits || [];
                                      setSelectedUserStats({ user: inv.user || {}, ...stats, dailyProfits });
                                    } catch (e) {
                                      console.error('Fetch daily profits failed', e.message);
                                      setSelectedUserStats({ user: inv.user || {}, ...stats, dailyProfits: [] });
                                    }
                                    setUserDetailOpen(true);
                                  }} className="w-full text-xs font-semibold px-3 py-1 rounded-xl bg-blue-600/15 text-blue-300 hover:bg-blue-600/25">User Detail</button>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <button
                                    onClick={() => handleToggleInvestmentStatus(inv)}
                                    className={`w-full text-xs font-semibold px-3 py-1 rounded-xl transition-colors ${inv.status === 'active' ? 'bg-red-600/15 text-red-300 hover:bg-red-600/25' : 'bg-green-600/15 text-green-300 hover:bg-green-600/25'}`}
                                  >
                                    {inv.status === 'active' ? 'Deactivate' : 'Activate'}
                                  </button>
                                  {/* <button onClick={() => {
                                    const stats = computeUserStats(inv.user?._id || inv.userId);
                                    setSelectedUserStats({ user: inv.user || {}, ...stats });
                                    setUserDetailOpen(true);
                                  }} className="w-full text-xs font-semibold px-3 py-1 rounded-xl bg-blue-600/15 text-blue-300 hover:bg-blue-600/25">User Detail</button> */}
                                </div>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trigger Modal */}
        {triggerModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-700 border border-white/10 rounded-2xl w-full max-w-md p-6">
              <h3 className="text-white font-bold text-lg mb-1">Set Monthly Return</h3>
              <p className="text-gray-400 text-sm mb-5">{triggerModal.name} — {currentMonth}</p>
              <div className="bg-dark-600 rounded-xl p-3 mb-4 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Package Price</span><span className="text-white">${triggerModal.price.toLocaleString()}</span></div>
                <div className="flex justify-between mt-1"><span className="text-gray-400">Max Allowed</span><span className="text-amber-400">Up to {triggerModal.monthlyReturnPercent}%</span></div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Actual Return % for this month</label>
                  <div className="relative">
                    <input type="number" value={triggerPercent} onChange={e => setTriggerPercent(e.target.value)}
                      className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-amber-500"
                      placeholder={`Max: ${triggerModal.monthlyReturnPercent}%`} max={triggerModal.monthlyReturnPercent} min="0.1" step="0.1" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                  </div>
                  {triggerPercent && (
                    <p className="text-primary-400 text-xs mt-1">
                      Each $1000 investment → ${(1000 * parseFloat(triggerPercent||0) / 100).toFixed(2)} return
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Note (optional)</label>
                  <input type="text" value={triggerNote} onChange={e => setTriggerNote(e.target.value)}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="e.g. Good market performance" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => { setTriggerModal(null); setTriggerPercent(''); setTriggerNote(''); }}
                  className="flex-1 bg-dark-600 border border-white/10 text-gray-400 hover:text-white py-3 rounded-xl transition-colors">Cancel</button>
                <button onClick={handleTrigger} disabled={triggerLoading}
                  className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
                  {triggerLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    : <><TrendingUp className="w-4 h-4" /> Set Return</>}
                </button>
              </div>
            </div>
          </div>
        )}
       
      </div>
    </AdminLayout>
  );
}
